/**
 * The approval chain, drawn — and the buttons for whoever's turn it is.
 *
 * One component for both work orders and variations. They run different chains
 * (five stages against three) but the chain is DATA, so the component reads it
 * rather than knowing it.
 *
 * The rule this screen exists to make obvious: at any moment exactly ONE
 * person can act, and everybody else can see who it is. The old paper order
 * was signed by two people whose roles were never stated, in an order nobody
 * recorded, months after it was dated.
 *
 * Every refusal here is also enforced in rpc_works_action. If the button is
 * missing the server would have said no anyway; the button is the courtesy.
 */
import { useMemo, useState } from 'react'
import { Check, CircleDashed, Clock, RotateCcw, X } from 'lucide-react'
import clsx from 'clsx'
import { useAuth } from '@/lib/auth'
import { useQuery, usePeople } from '@/lib/data'
import { supabase } from '@/lib/supabase'
import { dateTime } from '@/lib/format'
import { Card, Loading, Modal, Textarea, useToast } from '@/components/ui'

interface StepRow {
  doc_type: string; doc_id: string; step_no: number
  step_role: string; step_label: string; step_hint: string | null
  action: string | null; actor_email: string | null; actor_role: string | null
  is_standin: boolean | null; comment: string | null; acted_at: string | null
  state: 'DONE' | 'WAITING' | 'TO COME' | 'REJECT' | 'RETURN'
}

export function ApprovalChain({ docType, docId, status, currentStep, sitePmId, createdBy, onChanged }: {
  /**
   * 'RA' joined the other two on 15-09-2026. The component reads the chain out
   * of approval_chain_steps, so a third document type needed no new code here
   * beyond this type — which is the point of having built it that way.
   */
  /**
   * 'RETR' joined on 20-09-2026 — and again needed no new code here beyond
   * this type and a fourth arm in v_works_approval_progress, which is the
   * whole return on having made the chain data rather than code.
   */
  docType: 'WO' | 'WO_VARIATION' | 'RA' | 'RETR' | 'ADV' | 'CRN'
  docId: string
  status: string
  currentStep: number
  /** The Project Manager named on this site. Stage 2 of a work order is his. */
  sitePmId?: string | null
  createdBy?: string | null
  onChanged: () => void
}) {
  const { profile } = useAuth()
  const people = usePeople()
  const toast = useToast()
  const [asking, setAsking] = useState<'RETURN' | 'REJECT' | null>(null)
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)

  const { data: steps, loading, refresh } = useQuery<StepRow>(
    `chain_${docType}_${docId}`,
    () => supabase.from('v_works_approval_progress').select('*')
      .eq('doc_type', docType).eq('doc_id', docId).order('step_no'),
    [docType, docId, status, currentStep], { cacheable: false, idField: 'step_no' })

  /**
   * SINCE WHEN HAS IT BEEN SITTING THERE — added 16-09-2026.
   *
   * A cleared stage has always carried its timestamp: document_approvals
   * records who signed and when, and the view hands it over as acted_at. The
   * stage that is WAITING had nothing, because nobody has acted on it yet —
   * which is precisely the stage everybody opening the document wants a date
   * against. "With the Project Head" answers where it is; "since Tuesday
   * evening, four days" answers the question actually being asked, which is
   * whether somebody needs chasing.
   *
   * The moment the current stage began is the last SUBMIT or APPROVE in the
   * audit trail — the event that put it there. Read here rather than passed in,
   * so all three document types get it without their screens changing, and
   * keyed on status and current_step so it refreshes the moment the chain moves.
   */
  const { data: trail } = useQuery<any>(
    `chain_since_${docType}_${docId}`,
    () => supabase.from('document_audit').select('id, action, created_at, actor_email')
      .eq('doc_type', docType).eq('doc_id', docId)
      .order('created_at', { ascending: false }).limit(40),
    [docType, docId, status, currentStep], { cacheable: false })

  const since = useMemo(() => {
    const r = (trail as any[]).find(
      (x) => x.action === 'SUBMIT' || x.action === 'APPROVE' || x.action === 'RETURN')
    return (r?.created_at as string | undefined) ?? undefined
  }, [trail])

  /** Whole days, floored. "0 days" is today, and says so in words below. */
  const waitingDays = useMemo(() => {
    if (!since) return null
    const d = Math.floor((Date.now() - new Date(since).getTime()) / 86400000)
    return d < 0 ? 0 : d
  }, [since])

  if (loading) return <Loading label="Reading the chain…" />

  const waiting = steps.find((s) => s.state === 'WAITING')
  const role = profile?.role
  const uid = profile?.id

  // Whose turn is it, and is it mine?
  const isStandin = docType === 'WO' && waiting?.step_no === 1
    && role === 'project_head' && waiting.step_role === 'procurement_manager'
  const roleMatches = !!waiting && (role === waiting.step_role || isStandin || role === 'admin')
  // A stage that belongs to "the Project Manager" belongs to the one named on
  // the site — on a work order (stage 2) and on a running account bill
  // (stage 1) alike. rpc_works_action refuses any other manager in the same
  // words.
  const rightPm = !(waiting?.step_role === 'project_manager' && role === 'project_manager')
    || (!!sitePmId && sitePmId === uid)
  const isMine = status === 'SUBMITTED' && roleMatches && rightPm
  const iRaisedIt = !!createdBy && createdBy === uid

  async function act(action: 'APPROVE' | 'RETURN' | 'REJECT', comment?: string) {
    setBusy(true)
    try {
      const { data, error } = await supabase.rpc('rpc_works_action', {
        p_doc_type: docType, p_doc_id: docId, p_action: action, p_comment: comment ?? null,
      })
      if (error) throw new Error(error.message)
      toast.success(String(data ?? 'Done.'))
      setAsking(null); setReason('')
      refresh(); onChanged()
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not do that')
    } finally { setBusy(false) }
  }

  const ICON = {
    DONE: <Check className="h-4 w-4 text-emerald-600" />,
    WAITING: <Clock className="h-4 w-4 text-amber-600" />,
    'TO COME': <CircleDashed className="h-4 w-4 text-slate-300" />,
    REJECT: <X className="h-4 w-4 text-red-600" />,
    RETURN: <RotateCcw className="h-4 w-4 text-amber-600" />,
  }

  return (
    <Card title="Approval chain"
          subtitle={
            status === 'SUBMITTED' && waiting
              ? `Stage ${waiting.step_no} of ${steps.length} — with ${waiting.step_label}`
                + (since ? `, since ${dateTime(since)}` : '')
              : status === 'DRAFT' ? 'Not submitted yet'
              : status === 'RETURNED' ? 'Returned to the person who raised it'
              : status === 'REJECTED' ? 'Rejected'
              // CANCELLED arrived with rpc_vo_cancel on 16-09-2026 and fell
              // through to the line below, so an abandoned variation's card
              // read "Cleared all 2 stages" - which is the opposite of true.
              : status === 'CANCELLED' ? 'Cancelled before it went up the chain'
              : `Cleared all ${steps.length} stages`
          }>
      <ol className="space-y-2">
        {steps.map((s) => (
          <li key={s.step_no}
              className={clsx('flex items-start gap-3 rounded-lg border px-3 py-2',
                s.state === 'WAITING' ? 'border-amber-300 bg-amber-50'
                  : s.state === 'DONE' ? 'border-emerald-200 bg-emerald-50/40'
                  : s.state === 'REJECT' ? 'border-red-200 bg-red-50'
                  : 'border-slate-200')}>
            <span className="mt-0.5 shrink-0">{ICON[s.state] ?? ICON['TO COME']}</span>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-slate-800">
                {s.step_no}. {s.step_label}
              </p>
              {s.acted_at ? (
                <p className="text-xs text-slate-600">
                  {s.action === 'APPROVE' ? 'Approved' : s.action === 'RETURN' ? 'Returned' : 'Rejected'}
                  {/* 0217: a stage the board made automatic has no person behind
                      it, so it says so instead of naming nobody. */}
                  {s.actor_email
                    ? <>{' by '}{people.name({ email: s.actor_email })}{s.is_standin && ' (standing in)'}</>
                    : ' automatically'}
                  {' · '}{dateTime(s.acted_at)}
                </p>
              ) : (
                <>
                  <p className="text-xs text-slate-500">{s.step_hint}</p>
                  {s.state === 'WAITING' && since && (
                    <p className="mt-0.5 text-xs font-medium text-amber-800">
                      Waiting since {dateTime(since)}
                      {waitingDays !== null && (
                        <span className="font-normal">
                          {' — '}
                          {waitingDays === 0 ? 'today'
                            : waitingDays === 1 ? 'one day'
                            : `${waitingDays} days`}
                        </span>
                      )}
                    </p>
                  )}
                </>
              )}
              {s.comment && (
                <p className="mt-1 rounded bg-white/70 px-2 py-1 text-xs italic text-slate-600">
                  &ldquo;{s.comment}&rdquo;
                </p>
              )}
            </div>
          </li>
        ))}
      </ol>

      {/* ---------- the buttons, for exactly one person ---------- */}
      {status === 'SUBMITTED' && (
        <div className="mt-4 border-t border-slate-200 pt-4">
          {/*
            WHY THIS IS A SENTENCE AND NOT A BUTTON THAT FAILS.

            rpc_works_action refuses both of these, in these words. Drawing an
            Approve button and letting the server throw would show the same
            text in a red toast a second later - but it would also teach people
            that the buttons on this screen are a guess. A stage you cannot
            clear should look like one before you click it.

            The two cases read differently on purpose. Being told "you raised it
            yourself" at stage 1 as the Project Head is confusing, because he
            CAN normally act at stage 1 - he has a stand-in right there. What he
            needs to know is that the stand-in right is exactly the thing that
            stops at his own order.
          */}
          {isMine && iRaisedIt && isStandin && (
            <div className="rounded-lg border border-amber-300 border-l-4 border-l-amber-600 bg-amber-50 px-3 py-2">
              <p className="text-sm font-semibold text-amber-900">
                You raised {docId} yourself, so you cannot also clear the four-eyes
                stage on it.
              </p>
              <p className="mt-0.5 text-xs text-amber-800">
                Standing in for a Procurement Executive is meant for when one of them is
                away — not for checking your own work. This has to go to a Procurement
                Manager. Everything after stage&nbsp;1 is unaffected.
              </p>
            </div>
          )}
          {isMine && iRaisedIt && !isStandin && (
            <div className="rounded-lg border border-amber-300 border-l-4 border-l-amber-600 bg-amber-50 px-3 py-2">
              <p className="text-sm font-semibold text-amber-900">
                This is your stage, but you raised {docId} yourself.
              </p>
              <p className="mt-0.5 text-xs text-amber-800">
                Somebody else has to approve it. That is the whole point of the chain.
              </p>
            </div>
          )}
          {isMine && !iRaisedIt && (
            <div className="flex flex-wrap gap-2">
              <button className="btn-primary" disabled={busy} onClick={() => void act('APPROVE')}>
                <Check className="h-4 w-4" /> Approve
              </button>
              <button className="btn-secondary" disabled={busy} onClick={() => setAsking('RETURN')}>
                <RotateCcw className="h-4 w-4" /> Return for changes
              </button>
              <button className="btn-ghost text-red-600" disabled={busy} onClick={() => setAsking('REJECT')}>
                <X className="h-4 w-4" /> Reject
              </button>
            </div>
          )}
          {!isMine && waiting && (
            <p className="text-sm text-slate-500">
              Waiting for {waiting.step_label}. Nothing for you to do here.
            </p>
          )}
        </div>
      )}

      <Modal open={asking !== null} onClose={() => setAsking(null)}
             title={asking === 'REJECT' ? 'Reject this document' : 'Return it for changes'}
             subtitle={asking === 'REJECT'
               ? 'It cannot be revived. A rejected document stays on the record with your reason.'
               : 'It goes back to the person who raised it, and starts the chain again from stage 1.'}
             footer={
               <>
                 <button className="btn-secondary" onClick={() => setAsking(null)}>Cancel</button>
                 <button className={asking === 'REJECT' ? 'btn-danger' : 'btn-primary'}
                         disabled={busy || reason.trim().length < 4}
                         onClick={() => void act(asking!, reason.trim())}>
                   {asking === 'REJECT' ? 'Reject' : 'Return'}
                 </button>
               </>
             }>
        <p className="mb-2 text-sm text-slate-600">
          Say why. Without a reason the person who raised it learns nothing, and the
          audit trail even less.
        </p>
        <Textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)}
                  placeholder="The rate for RCC is above what we paid at Unity Mall last month." />
      </Modal>
    </Card>
  )
}
